import { useState, useCallback } from 'react';
import type {
  GroupMember,
  GroupInvite,
  GroupTrip,
  ItineraryVote,
  GroupActivity,
  GroupBudget,
  GroupChatMessage,
  GroupNotification,
  FamilyTripSettings,
  GroupAnalytics,
  Vote,
  MemberPreferences
} from '../types/group';

interface UseGroupTripOptions {
  tripId: string;
  currentUserId: string;
}

interface UseGroupTripReturn {
  trip: GroupTrip | null;
  isLoading: boolean;
  error: string | null;
  members: GroupMember[];
  addMember: (email: string, role: GroupMember['role']) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  updateMemberRole: (memberId: string, role: GroupMember['role']) => Promise<void>;
  updateMemberPreferences: (memberId: string, preferences: Partial<MemberPreferences>) => Promise<void>;
  inviteMember: (email: string, role: GroupMember['role']) => Promise<string>;
  acceptInvite: (inviteCode: string) => Promise<void>;
  votes: ItineraryVote[];
  createVote: (itemId: string) => Promise<void>;
  castVote: (voteId: string, voteType: Vote['voteType'], comment?: string) => Promise<void>;
  closeVote: (voteId: string) => Promise<void>;
  getVoteStatus: (itemId: string) => ItineraryVote | null;
  activities: GroupActivity[];
  proposeActivity: (activity: any) => Promise<void>;
  approveActivity: (activityId: string) => Promise<void>;
  rateActivitySuitability: (activityId: string, scores: any[]) => Promise<void>;
  getSuitableActivities: (memberId: string) => GroupActivity[];
  budget: GroupBudget | null;
  setBudget: (totalBudget: number) => Promise<void>;
  contributeToBudget: (amount: number) => Promise<void>;
  allocateBudget: (category: string, amount: number) => Promise<void>;
  messages: GroupChatMessage[];
  sendMessage: (content: string, type?: GroupChatMessage['type']) => Promise<void>;
  notifications: GroupNotification[];
  markNotificationRead: (notificationId: string) => Promise<void>;
  clearNotifications: () => Promise<void>;
  settings: FamilyTripSettings | null;
  updateSettings: (settings: Partial<FamilyTripSettings>) => Promise<void>;
  analytics: GroupAnalytics | null;
  canEdit: boolean;
  canVote: boolean;
  canManageMembers: boolean;
  isOwner: boolean;
}

const defaultSettings: FamilyTripSettings = {
  kidFriendlyMode: false,
  teenActivitiesEnabled: true,
  mixedAgeActivities: true,
  budgetVisibility: 'public',
  votingRequired: false,
  requireApproval: false,
  activityLevel: 'balanced',
  mealPreferences: {
    breakfastIncluded: false,
    lunchIncluded: false,
    dinnerIncluded: false,
    dietaryRequirements: []
  },
  transportPreferences: {
    privateTransport: false,
    publicTransport: true,
    walkingEnabled: true,
    restStops: 2
  }
};

export function useGroupTrip({ tripId, currentUserId }: UseGroupTripOptions): UseGroupTripReturn {
  const [trip, setTrip] = useState<GroupTrip | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [votes, setVotes] = useState<ItineraryVote[]>([]);
  const [activities, setActivities] = useState<GroupActivity[]>([]);
  const [budget, setBudget] = useState<GroupBudget | null>(null);
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [notifications, setNotifications] = useState<GroupNotification[]>([]);
  const [settings, setSettings] = useState<FamilyTripSettings | null>(null);

  const currentMember = members.find(m => m.userId === currentUserId);
  const canEdit = currentMember?.role === 'owner' || currentMember?.role === 'admin' || currentMember?.role === 'editor';
  const canVote = currentMember?.role !== 'viewer';
  const canManageMembers = currentMember?.role === 'owner' || currentMember?.role === 'admin';
  const isOwner = currentMember?.role === 'owner';

  const addMember = useCallback(async (email: string, role: GroupMember['role']) => {
    const newMember: GroupMember = {
      id: `member_${Date.now()}`,
      tripId,
      userId: `user_${Date.now()}`,
      email,
      displayName: email.split('@')[0],
      role,
      status: 'active',
      joinedAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString()
    };
    setMembers(prev => [...prev, newMember]);
  }, [tripId]);

  const removeMember = useCallback(async (memberId: string) => {
    setMembers(prev => prev.filter(m => m.id !== memberId));
  }, []);

  const updateMemberRole = useCallback(async (memberId: string, role: GroupMember['role']) => {
    setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role } : m));
  }, []);

  const updateMemberPreferences = useCallback(async (memberId: string, preferences: Partial<MemberPreferences>) => {
    setMembers(prev => prev.map(m => m.id === memberId ? { ...m, preferences: { ...m.preferences, ...preferences } } : m));
  }, []);

  const inviteMember = useCallback(async (email: string, role: GroupMember['role']): Promise<string> => {
    const inviteCode = `${tripId.slice(0, 8)}_${Date.now().toString(36)}`;
    return inviteCode;
  }, [tripId]);

  const acceptInvite = useCallback(async (inviteCode: string) => {
    console.log(`Accepting invite: ${inviteCode}`);
  }, []);

  const createVote = useCallback(async (itemId: string) => {
    const vote: ItineraryVote = {
      id: `vote_${Date.now()}`,
      tripId,
      itemId,
      votes: [],
      status: 'open',
      createdAt: new Date().toISOString()
    };
    setVotes(prev => [...prev, vote]);
  }, [tripId]);

  const castVote = useCallback(async (voteId: string, voteType: Vote['voteType'], comment?: string) => {
    const vote: Vote = {
      id: `vote_cast_${Date.now()}`,
      memberId: currentUserId,
      voteType,
      comment,
      votedAt: new Date().toISOString()
    };
    setVotes(prev => prev.map(v => v.id === voteId ? { ...v, votes: [...v.votes, vote] } : v));
  }, [currentUserId]);

  const closeVote = useCallback(async (voteId: string) => {
    setVotes(prev => prev.map(v => v.id === voteId ? { ...v, status: 'approved' as const, closedAt: new Date().toISOString() } : v));
  }, []);

  const getVoteStatus = useCallback((itemId: string) => {
    return votes.find(v => v.itemId === itemId && v.status === 'open') || null;
  }, [votes]);

  const proposeActivity = useCallback(async (activityData: any) => {
    const activity: GroupActivity = {
      ...activityData,
      id: `activity_${Date.now()}`,
      tripId,
      createdAt: new Date().toISOString(),
      suitabilityScores: [],
      totalSuitability: 0
    };
    setActivities(prev => [...prev, activity]);
  }, [tripId]);

  const approveActivity = useCallback(async (activityId: string) => {
    setActivities(prev => prev.map(a => a.id === activityId ? { ...a, status: 'approved' } : a));
  }, []);

  const rateActivitySuitability = useCallback(async (activityId: string, scores: any[]) => {
    const suitabilityScores = scores.map(s => ({
      memberId: s.memberId,
      memberName: members.find(m => m.id === s.memberId)?.displayName || 'Unknown',
      score: s.score
    }));
    const total = suitabilityScores.reduce((sum, s) => sum + (s.score === 'excellent' ? 5 : s.score === 'good' ? 4 : s.score === 'moderate' ? 3 : s.score === 'poor' ? 2 : 1), 0);
    setActivities(prev => prev.map(a => a.id === activityId ? { ...a, suitabilityScores, totalSuitability: total / suitabilityScores.length } : a));
  }, [members]);

  const getSuitableActivities = useCallback((memberId: string) => {
    return activities;
  }, [activities]);

  const setBudgetTotal = useCallback(async (totalBudget: number) => {
    const newBudget: GroupBudget = {
      totalBudget, spent: 0, currency: 'USD', allocations: [], contributions: [], byMember: []
    };
    setBudget(newBudget);
  }, []);

  const contributeToBudget = useCallback(async (amount: number) => {
    if (!budget || !currentMember) return;
    const contribution = {
      memberId: currentUserId,
      memberName: currentMember.displayName,
      amount, currency: budget.currency,
      contributedAt: new Date().toISOString()
    };
    setBudget(prev => prev ? { ...prev, contributions: [...prev.contributions, contribution] } : null);
  }, [budget, currentMember, currentUserId]);

  const allocateBudget = useCallback(async (category: string, amount: number) => {
    if (!budget) return;
    const allocation = { category, amount, percentage: (amount / budget.totalBudget) * 100 };
    setBudget(prev => prev ? { ...prev, allocations: [...prev.allocations, allocation] } : null);
  }, [budget]);

  const sendMessage = useCallback(async (content: string, type: GroupChatMessage['type'] = 'text') => {
    const message: GroupChatMessage = {
      id: `msg_${Date.now()}`,
      tripId,
      memberId: currentUserId,
      memberName: members.find(m => m.userId === currentUserId)?.displayName || 'Unknown',
      content,
      type,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, message]);
  }, [tripId, currentUserId, members]);

  const markNotificationRead = useCallback(async (notificationId: string) => {
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
  }, []);

  const clearNotifications = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const updateSettings = useCallback(async (newSettings: Partial<FamilyTripSettings>) => {
    setSettings(prev => prev ? { ...prev, ...newSettings } : { ...defaultSettings, ...newSettings });
  }, []);

  const analytics: GroupAnalytics | null = members.length > 0 ? {
    totalMembers: members.length,
    activeMembers: members.filter(m => m.status === 'active').length,
    activitiesProposed: activities.length,
    activitiesApproved: activities.filter(a => a.status === 'approved' || a.status === 'booked').length,
    totalVotes: votes.reduce((sum, v) => sum + v.votes.length, 0),
    averageParticipation: 0,
    budgetUtilization: 0,
    topContributor: 'N/A',
    mostActiveMember: members.sort((a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime())[0]?.displayName || 'N/A'
  } : null;

  return {
    trip, isLoading, error, members, addMember, removeMember, updateMemberRole, updateMemberPreferences,
    inviteMember, acceptInvite, votes, createVote, castVote, closeVote, getVoteStatus, activities,
    proposeActivity, approveActivity, rateActivitySuitability, getSuitableActivities, budget,
    setBudget: setBudgetTotal, contributeToBudget, allocateBudget, messages, sendMessage,
    notifications, markNotificationRead, clearNotifications, settings: settings || defaultSettings,
    updateSettings, analytics, canEdit, canVote, canManageMembers, isOwner
  };
}

export default useGroupTrip;
